import * as React from 'react';
import { NS } from '@/common/namespaces';
import { InputUnits, SvgAnnotations } from '../components';
import SVGEditor from '../editor/Editor';
import * as SVGMath from '../svgcanvas/math';
import SVGCanvas from '../svgcanvas/svgcanvas';
import * as utils from '../svgcanvas/utilities';
import { isElementInLockedLayer, isElementEffectivelyLocked } from '../svgcanvas/layer-tools';
import { OnUpdateEvent, SubscriberService, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import { panelsServiceInstance } from "./panels-service";
import { getPanelPlaceholderID } from "@/common/utilities";
import { elementToJson } from '@/common/utilities';

export default class SvgEditService extends SubscriberService<SvgEditService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return svgEditServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public editor: SVGEditor = null as any;
    public canvas: SVGCanvas = null as any;
    public selectedElements: SVGGraphicsElement[] = [];

    private oldSelectionChanged: (win: Window, elems: SVGGraphicsElement[]) => void;
    private readyEvents: (() => void)[] = [];
    private hasDoneReadyEvents: boolean = false;

    private _subscribedSelectionChangesObservers: React.Dispatch<void>[] = [];

    constructor() {
        super();

        window.addEventListener("svgcontent-changed", () => {
            setTimeout(() => {
                this.ElementStateHasChanged();
            }); // 1 tick delay to let things settle.
        });
    }

    public InitializeEditor(svgEditor: SVGEditor) {
        this.editor = svgEditor;
        this.editor.refreshUI = () => this._UpdateSubscribers();
        this.oldSelectionChanged = this.editor.selectedChanged.bind(this.editor);
        this.editor.selectedChanged = (win, elems) => this._OnSelectionChanged(win, elems);
    }

    public InitializeCanvas(svgCanvas: SVGCanvas) {
        this.canvas = svgCanvas;
        this.BindCanvasEvents();
        SvgAnnotations.BindAnnotations(this.canvas, this.canvas.getRootElem());

        for (const ev of this.readyEvents) {
            ev();
        }
        this.hasDoneReadyEvents = true;

        this._UpdateSubscribers();
    }

    /**
     * Called when everything is in place and ready to go (For example, editor and canvas are both valid values.)
     * @param ev The action to call when SVGEdit is ready.
     */
    public RegisterReadyEvent(ev: () => void) {
        if (this.hasDoneReadyEvents) {
            ev();
        } else {
            this.readyEvents.push(ev);
        }
    }

    public hasCanvas() {
        return this.canvas !== null;
    }

    public hasSelectedElements() {
        return this.selectedElements.length > 0;
    }

    public hasSelectedMultipleElements() {
        return this.selectedElements.length > 1;
    }

    public hasSelectedGroupElement() {
        return this.selectedElements.length > 0 
            && this.selectedElements[0].tagName.toLowerCase() === "g" 
            && !this.selectedElements[0].classList.contains("no-ungroup");
    }

    public isWireframeActive() {
        return this.editor?.wireframeActive ?? false;
    }

    private BindCanvasEvents() {
        const canvas = document.getElementById("svgcanvas");
        canvas.addEventListener("wheel", e => this.OnCanvasScroll(e));
        canvas.addEventListener("mouseup", e => this.OnCanvasMouseUp(e));

        this.canvas.bind('zoomDone', () => {
            this._UpdateSubscribers();
        });
    }

    private OnCanvasScroll(event: WheelEvent) {
        if (!this.editor.configObj.getBoolPref("mouseWheelZoomEnabled")) {
            return;
        }

        event.preventDefault();

        // Derive the zoom direction from the SIGN of the scroll, not its magnitude.
        // WheelEvent.deltaY is only large (~±100) in pixel deltaMode; in line/page
        // deltaMode (common on many mice/drivers) a notch reports ~±3, which the old
        // magnitude-based factor clamped below 1.0 in BOTH directions - so every
        // scroll zoomed out and zoom-in never happened. Using the sign is robust
        // across all deltaMode values.
        let dir = 0;
        if (event.deltaY) {
            dir = -Math.sign(event.deltaY);
        } else if (event.detail) {
            dir = -Math.sign(event.detail);
        }

        if (!dir) {
            return;
        }

        const groupElements = document.querySelectorAll("#svgcontent g");
        let firstValidGroupElement: SVGGElement | null;

        for (const el of groupElements) {
            if (el.namespaceURI.endsWith("://www.w3.org/2000/svg")) {
                firstValidGroupElement = el as SVGGElement;
                break;
            }
        }

        if (!firstValidGroupElement) {
            console.warn("Could not find a <g> element on #svgcontent");
            return;
        }

        const root_sctm = firstValidGroupElement.getScreenCTM().inverse();
        // Anchor the zoom on the actual cursor position (same approach as the Shift+wheel
        // DOMMouseScrollEvent handler). The previous calculation averaged the cursor with the
        // window centre, which biased the focal point and made the canvas drift on every zoom
        // step (creeping upward when zooming with the cursor in the upper half of the canvas).
        const pt = SVGMath.transformPoint(event.pageX, event.pageY, root_sctm);

        const bbox = {
            'x': pt.x,
            'y': pt.y,
            'width': 0,
            'height': 0,
            'factor': dir > 0 ? 4 / 3.5 : 3.5 / 4,
        };

        (this.canvas as any).call('zoomed', bbox);

        SvgAnnotations.UpdateAnnotations();
    }

    private OnCanvasMouseUp(event: MouseEvent) {
        if (this.canvas.getMode() === "select" && (event.target as Element).closest("#svgcontent")) {
            setTimeout(() => {
                (document.activeElement as HTMLElement).blur();
            });
        }
    }

    private _OnSelectionChanged(win: Window, elems: SVGGraphicsElement[]) {
        this.oldSelectionChanged(win, elems);

        const oldSelectedElems = this.selectedElements;
        this.selectedElements = [];
        for (const el of elems) {
            // SVGEdit can sometimes have null elements in its own selected elements list.
            if (el != null) {
                this.selectedElements.push(el);
            }
        }

        if (this.selectedElements.length === oldSelectedElems.length && this.selectedElements.every(e => oldSelectedElems.indexOf(e) >= 0)) {
            return; // No changes - no need to update subscribers
        }

        this._UpdateSubscribers();
        for (const observer of this._subscribedSelectionChangesObservers) {
            observer();
        }

        const actionEnabledSelection = this.selectedElements.filter(el => !isElementEffectivelyLocked(el));
        const ev = new CustomEvent("selection-changed", { detail: actionEnabledSelection });
        window.dispatchEvent(ev);
    }

    public AddSvgElementFromString(str: string, attr?: { [key: string]: any }): SVGGElement {
        const anyCanv = this.canvas as any; // Some methods aren't appearing in TS due to the funky setup.

        const curLayer = anyCanv.getCurrentDrawing().getCurrentLayer() as SVGElement;
        const tempContainer = document.createElementNS("http://www.w3.org/2000/svg", "g");
        tempContainer.innerHTML = str;
        const el = tempContainer.firstChild as SVGElement;
        curLayer.appendChild(el);
        utils.cleanupElement(el);
        const container = anyCanv.groupSvgElem(el);
        if (attr) {
            utils.assignAttributes(container, attr, 100);
        }

        return container;
    }

    public ElementStateHasChanged(elements?: SVGGraphicsElement | SVGGraphicsElement[]) {
        this._UpdateSubscribers();
        SvgAnnotations.UpdateAnnotations();
    }

    public static GetNextElementID(): string {
        return svgEditServiceInstance.canvas.getCurrentDrawing().getNextId();
    }

    public fixupExternalElementReferences() {
        const fixupRecur = (el: SVGElement) => {
            if (el.tagName.toLowerCase() === "use") {
                const compID = el.getAttribute("zaa:component-reference");
                const elRef = panelsServiceInstance.GetPanelDetailsFromReference(compID);
                if (elRef) {
                    el.setAttribute("href", `#${getPanelPlaceholderID(elRef)}`);
                }
            }

            for (const child of el.children) {
                fixupRecur(child as SVGElement);
            }
        }

        fixupRecur(this.canvas.getSVGContent());
    }

    public getElementCTM(elementId: string, fromBlazor: boolean = false) {
        if (elementId === null || elementId.trim() === "") {
            return null;
        }

        const el = document.getElementById(elementId) as unknown as SVGGraphicsElement;
        if (!el) {
            return null;
        }

        const ctm = el.getCTM();
        if (!fromBlazor || !ctm) {
            return ctm;
        }

        // Need to create a new object for Blazor as the CTM can't beserailized otherwise.
        return {
            a: ctm.a,
            b: ctm.b,
            c: ctm.c,
            d: ctm.d,
            e: ctm.e,
            f: ctm.f,
        };
    }

    public getSelectedElementCTM(fromBlazor: boolean = false) {
        if (this.selectedElements.length === 0) {
            return null;
        }

        return this.getElementCTM(this.selectedElements[0].id, fromBlazor);
    }

    /**
     * Gets the raw `zaa:dpi` attribute of the document root, or null if it has none. Called from Blazor, which applies
     * the default DPI and parses the value.
     */
    public getDocumentDpi(): string | null {
        return this.canvas.getSVGContent()?.getAttribute("zaa:dpi") ?? null;
    }

    /**
     * Gets everything Blazor needs to convert a text element's font size, in a single interop call.
     * @param elementId The text element to get the transform of, or null for the first selected element.
     */
    public getTextScaleContext(elementId: string | null) {
        return {
            dpi: this.getDocumentDpi(),
            zoom: this.canvas.getCurrentZoom(),
            ctm: elementId === null
                ? this.getSelectedElementCTM(true)
                : this.getElementCTM(elementId, true),
        };
    }

    public getElementAsJson(elementID: string, childDepth: number = 1) {
        const el = document.getElementById(elementID);
        if (!el) {
            return null;
        }

        return elementToJson(el, childDepth);
    }

    public getSelectedElementsAsJson(childDepth: number = 1) {
        return this.selectedElements.map(e => elementToJson(e, childDepth));
    }

    public getElementDimensions(elementID: string) {
        const el = document.getElementById(elementID);
        const rect = el?.getBoundingClientRect();
        if (rect) {
            return {
                x: rect.left + window.scrollX,
                y: rect.top + window.scrollY,
                width: rect.width,
                height: rect.height,
            };
        }

        return null;
    }
}

export const svgEditServiceInstance = createStaticServiceInstance(new SvgEditService(), "svgEditService");

// For React Functional Components
export const useSvgEditService = createUseZenAutoArtService(svgEditServiceInstance);
